import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, Users, FileCheck, AlertTriangle, Wallet, Scissors,
  Clock, TrendingUp, RefreshCw, Loader2, Search, ShieldCheck,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { confirm } from "@/lib/confirm";
import ErrorBoundary from "@/components/ErrorBoundary";
import { safeBRL, safeNumber } from "@/lib/formatters";
import BvsmResultCard from "./dashboard/BvsmResultCard";
import AgendadasRealizadasCard from "./dashboard/AgendadasRealizadasCard";
import WeeklySummaryCard from "./dashboard/WeeklySummaryCard";
import OrphansPanel from "./dashboard/OrphansPanel";
import NightlyAuditBanner from "./dashboard/NightlyAuditBanner";
import InstallPwaPrompt from "@/components/InstallPwaPrompt";

function Kpi({ icon: Icon, label, value, tone = "blue", testid, to }) {
  const tones = {
    blue: "text-blue-700 bg-blue-50 border-blue-100",
    green: "text-emerald-700 bg-emerald-50 border-emerald-100",
    amber: "text-amber-700 bg-amber-50 border-amber-100",
    red: "text-red-700 bg-red-50 border-red-100",
    slate: "text-slate-700 bg-slate-50 border-slate-100",
  };
  const inner = (
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`h-10 w-10 rounded-md grid place-items-center border ${tones[tone] || tones.blue}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        <div className="font-display text-2xl font-semibold text-slate-900 mt-0.5">{value}</div>
      </div>
    </CardContent>
  );
  return to ? (
    <Link to={to} className="block">
      <Card data-testid={testid} className="border-slate-200 shadow-sm hover:border-blue-400 hover:shadow transition-all cursor-pointer">{inner}</Card>
    </Link>
  ) : (
    <Card data-testid={testid} className="border-slate-200 shadow-sm">{inner}</Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "admin_master";
  const [data, setData] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [orphans, setOrphans] = useState(null); // null | [] | [...]
  const [orphansLoading, setOrphansLoading] = useState(false);

  function reloadDashboard() {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }

  useEffect(() => { reloadDashboard(); }, []);

  async function syncAll() {
    if (!isAdmin || syncLoading) return;
    if (!(await confirm({ description: "Sincronizar tudo?\n\n" +
      "Vai:\n• Ressincronizar TODOS os casos com os dados atuais dos mapas filtrados (equipe, procedimento, execução, pro bono)\n" +
      "• Marcar como confirmados todos os casos executados ainda pendentes\n" +
      "• Reclassificar como suspensos os casos com executed=false\n" +
      "• Atualizar os KPIs do painel\n\nNão apaga nada. É seguro rodar a qualquer hora." }))) return;
    setSyncLoading(true);
    try {
      const r = await api.post("/admin/data/sync-all", {});
      const d = r.data || {};
      const t = d.totals || {};
      toast.success(
        `Sincronizado: ${d.cases_resynced ?? 0} casos ressincronizados com os mapas, ${d.cases_backfilled} confirmados, ${d.cases_marked_suspended} suspensos. ` +
        `Total: ${t.in_production_billing_financial} casos em Produção/Faturamento/Financeiro.`,
        { duration: 6000 }
      );
      if (d.rows_without_case > 0) {
        toast.warning(
          `${d.rows_without_case} linha(s) incluída(s) nos mapas filtrados ainda sem caso gerado — abra o mapa e clique em "Gerar Casos".`,
          { duration: 8000 }
        );
      }
      reloadDashboard();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha na sincronização");
    } finally { setSyncLoading(false); }
  }

  async function reconcileHistory() {
    if (!isAdmin || reconcileLoading) return;
    if (!(await confirm({ description: "Reconciliar TODO o histórico?\n\n" +
      "O mapa filtrado mais recente de cada dia+hospital vira a única verdade:\n" +
      "• Remove mapas duplicados do mesmo dia+hospital (mantém o mais recente)\n" +
      "• Remove casos duplicados/obsoletos de mapas antigos ou apagados\n" +
      "• Limpa cirurgiões/anestesistas fantasmas (excluídos) dos casos e mapas\n\n" +
      "Hospitais diferentes no mesmo dia NÃO se afetam. Casos manuais (sem mapa) são preservados.\n\n" +
      "Esta ação apaga dados obsoletos permanentemente. Continuar?" }))) return;
    setReconcileLoading(true);
    try {
      const r = await api.post("/admin/data/reconcile-history", {});
      const d = r.data || {};
      toast.success(
        `Histórico reconciliado: ${d.cases_removed_total ?? 0} caso(s) obsoleto(s) removido(s), ` +
        `${d.duplicate_map_count ?? 0} mapa(s) duplicado(s) removido(s), ` +
        `${(d.ghost_case_refs_cleaned ?? 0) + (d.ghost_row_refs_cleaned ?? 0)} referência(s) fantasma limpa(s).`,
        { duration: 8000 }
      );
      (d.removed_by_day || []).slice(0, 8).forEach((x) =>
        toast.info(`${x.day}: ${x.removed} caso(s) removido(s)`, { duration: 8000 }));
      reloadDashboard();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao reconciliar histórico");
    } finally { setReconcileLoading(false); }
  }

  async function loadOrphans() {
    if (!isAdmin || orphansLoading) return;
    setOrphansLoading(true);
    try {
      const r = await api.get("/admin/data/case-orphans");
      const list = r.data?.orphans || [];
      setOrphans(list);
      list.length === 0
        ? toast.success("Nenhum caso órfão. Todos os casos têm linha correspondente no mapa filtrado.")
        : toast.warning(`${list.length} caso(s) órfão(s) encontrado(s). Veja a lista abaixo.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao buscar órfãos");
    } finally { setOrphansLoading(false); }
  }

  async function deleteOrphan(c) {
    if (!isAdmin) return;
    if (!(await confirm({ description: `Excluir o caso órfão ${c.case_code}?\n\n` +
      `Paciente: ${c.patient_name}\nProcedimento: ${c.procedure_name}\n\nEsta ação é permanente.` }))) return;
    try {
      await api.delete(`/cases/${c.id}`);
      toast.success(`Caso ${c.case_code} excluído.`);
      setOrphans((prev) => (prev || []).filter((x) => x.id !== c.id));
      reloadDashboard();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao excluir caso");
    }
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* PWA install prompt (mobile only, hides if already installed or recently dismissed) */}
      <InstallPwaPrompt />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Painel</h1>
          <p className="text-sm text-slate-500 mt-1">Visão geral da produção cirúrgica. Clique nos indicadores para detalhar.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={loadOrphans} disabled={orphansLoading} data-testid="btn-find-orphans"
              className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800 h-11 md:h-9"
              title="Lista casos no banco que não têm mais linha correspondente no mapa filtrado">
              {orphansLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Casos órfãos
            </Button>
            <Button variant="outline" size="sm" onClick={syncAll} disabled={syncLoading} data-testid="btn-sync-all"
              className="border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 h-11 md:h-9"
              title="Reconfere e atualiza os totais entre Produção, Faturamento e Financeiro">
              {syncLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sincronizar tudo
            </Button>
            <Button variant="outline" size="sm" onClick={reconcileHistory} disabled={reconcileLoading} data-testid="btn-reconcile-history"
              className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800 h-11 md:h-9"
              title="Remove do histórico inteiro casos duplicados de mapas antigos e referências a profissionais excluídos — o mapa mais recente de cada dia+hospital vira a única verdade">
              {reconcileLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Reconciliar histórico
            </Button>
          </div>
        )}
      </div>

      {isAdmin && <NightlyAuditBanner />}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={Users} tone="blue" label="Pacientes" value={safeNumber(data?.total_patients)} testid="kpi-patients" to="/pacientes" />
        <Kpi icon={Scissors} tone="green" label="Casos totais" value={safeNumber(data?.total_cases)} testid="kpi-cases" to="/casos" />
        <Kpi icon={Clock} tone="amber" label="Hoje" value={safeNumber(data?.cases_today)} testid="kpi-today" to="/casos?today=1" />
        <Kpi icon={FileCheck} tone="slate" label="Aguardando upload" value={safeNumber(data?.cases_pending_docs)} testid="kpi-pending" to="/casos?status=documentos_a_gerar" />
        <Kpi icon={Activity} tone="blue" label="Aguardando assinados" value={safeNumber(data?.cases_awaiting_signed)} testid="kpi-awaiting-signed" to="/casos?status=aguardando_assinados" />
        <Kpi icon={AlertTriangle} tone="red" label="Divergências" value={safeNumber(data?.cases_with_discrepancy)} testid="kpi-discrepancy" to="/casos?discrepancy=1" />
        <Kpi icon={Wallet} tone="green" label="Prontos p/ faturar" value={safeNumber(data?.cases_ready_billing)} testid="kpi-ready-billing" to="/casos?status=pronto_faturamento" />
        <Kpi icon={TrendingUp} tone="slate" label="Faturados" value={safeNumber(data?.cases_billed)} testid="kpi-billed" to="/casos?status=faturado" />
      </div>

      <ErrorBoundary inline label="Painel financeiro indisponível" testid="bvsm-card-error">
        <BvsmResultCard isAdmin={isAdmin} />
      </ErrorBoundary>

      <OrphansPanel orphans={orphans} onClose={() => setOrphans(null)} onDelete={deleteOrphan} />

      <ErrorBoundary inline label="Painel Agendadas × Realizadas indisponível" testid="agendadas-card-error">
        <AgendadasRealizadasCard data={data} />
      </ErrorBoundary>

      <ErrorBoundary inline label="Resumo semanal por IA indisponível" testid="weekly-card-error">
        <WeeklySummaryCard />
      </ErrorBoundary>

      {/* Produção por cirurgião + Financeiro side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-lg font-semibold text-slate-900">Produção por cirurgião</h2>
                <p className="text-sm text-slate-500 mt-1">Total de casos vinculados. Clique para ver os casos.</p>
              </div>
              <Link to="/producao" className="text-xs text-blue-700 hover:underline font-medium" data-testid="link-producao">Ver produção completa →</Link>
            </div>
            <div className="mt-5 space-y-3">
              {(data?.production_by_surgeon || []).length === 0 && (
                <div className="text-sm text-slate-500 py-8 text-center">Sem dados de produção ainda.</div>
              )}
              {(data?.production_by_surgeon || []).map((p, idx) => {
                const max = Math.max(...(data?.production_by_surgeon || []).map((x) => x.total), 1);
                const pct = Math.round((p.total / max) * 100);
                return (
                  <Link key={p.surgeon_id} to={`/casos?surgeon_id=${p.surgeon_id}`}
                    className="flex items-center gap-3 p-2 -mx-2 rounded-md hover:bg-slate-50 transition-colors cursor-pointer"
                    data-testid={`prod-row-${idx}`}>
                    <div className="w-48 text-sm font-medium text-slate-700 truncate">{p.name}</div>
                    <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-blue-700" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-10 text-right text-sm font-semibold text-slate-900">{p.total}</div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-slate-900">Financeiro</h2>
              <Link to="/financeiro" className="text-xs text-blue-700 hover:underline font-medium" data-testid="link-financeiro">Rateio por turno →</Link>
            </div>
            <dl className="mt-5 space-y-1">
              <Link to="/faturamento" className="block -mx-3 px-3 py-2 rounded-md hover:bg-slate-50 transition-colors" data-testid="link-fin-approved">
                <dt className="text-xs uppercase tracking-wider text-slate-500">Aprovado</dt>
                <dd className="font-display text-2xl font-semibold text-slate-900">{safeBRL(data?.financials?.approved)}</dd>
              </Link>
              <Link to="/faturamento?status=faturado" className="block -mx-3 px-3 py-2 rounded-md hover:bg-slate-50 transition-colors" data-testid="link-fin-billed">
                <dt className="text-xs uppercase tracking-wider text-slate-500">Faturado</dt>
                <dd className="font-display text-2xl font-semibold text-slate-900">{safeBRL(data?.financials?.billed)}</dd>
              </Link>
              <Link to="/faturamento?status=faturado" className="block -mx-3 px-3 py-2 rounded-md hover:bg-slate-50 transition-colors" data-testid="link-fin-received">
                <dt className="text-xs uppercase tracking-wider text-slate-500">Recebido</dt>
                <dd className="font-display text-2xl font-semibold text-emerald-700">{safeBRL(data?.financials?.received)}</dd>
              </Link>
            </dl>
            <div className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-500">
              Valores por caso são editáveis na aba Financeiro do caso.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Atalhos */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <h2 className="font-display text-lg font-semibold text-slate-900">Atalhos</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Link to="/mapa-bruto" className="border border-slate-200 rounded-md p-4 hover:border-blue-400 hover:bg-blue-50/40 transition-colors" data-testid="shortcut-raw-map">
              <div className="text-sm font-semibold text-slate-900">Importar mapa bruto</div>
              <div className="text-xs text-slate-500 mt-1">Upload PDF, texto ou manual</div>
            </Link>
            <Link to="/mapa-filtrado" className="border border-slate-200 rounded-md p-4 hover:border-blue-400 hover:bg-blue-50/40 transition-colors" data-testid="shortcut-filtered-map">
              <div className="text-sm font-semibold text-slate-900">Mapa filtrado</div>
              <div className="text-xs text-slate-500 mt-1">Só sua equipe, estilo BV</div>
            </Link>
            <Link to="/casos" className="border border-slate-200 rounded-md p-4 hover:border-blue-400 hover:bg-blue-50/40 transition-colors" data-testid="shortcut-cases">
              <div className="text-sm font-semibold text-slate-900">Casos cirúrgicos</div>
              <div className="text-xs text-slate-500 mt-1">Gerar, assinar, faturar</div>
            </Link>
            <Link to="/pacientes" className="border border-slate-200 rounded-md p-4 hover:border-blue-400 hover:bg-blue-50/40 transition-colors" data-testid="shortcut-patients">
              <div className="text-sm font-semibold text-slate-900">Pacientes</div>
              <div className="text-xs text-slate-500 mt-1">Cadastro e histórico</div>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
